import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Stack } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  useGetApiServiceListQuery,
  useGetApiServiceMetricsRolling24hQuery,
} from '../../../../redux/api';
import { K } from '../apiServiceI18n';
import ApiServicePerformanceToolbar from './ApiServicePerformanceToolbar';
import ApiServicePerformanceSingularView from './ApiServicePerformanceSingularView';
import ApiServiceStackedColumnChart from './ApiServiceStackedColumnChart';
import {
  DEFAULT_PERF_FILTERS,
  TIMEFRAME_1D,
  TIMEFRAME_CUSTOM,
  buildFilterOptionsFromServices,
  buildMetricsQueryParams,
  buildServiceNameMap,
  buildServiceAccountMap,
  defaultCustomRange,
  filtersAreDefault,
  metricsSuccess,
  rangeForPreset,
  aggregateBySourceToOutliers,
  buildDetailTargetFromBar,
  transformBySourceToChart,
  transformOutliersToChart,
  isAllSelected,
  isMetricsQueryPending,
} from './apiServicePerformanceUtils';

function ApiServicePerformanceDashboard({ seedApi, onSeedConsumed }) {
  const { t } = useTranslation();
  const { account: baseAccount } = useSelector((state) => state.queryParams);

  const [timeframe, setTimeframe] = useState(TIMEFRAME_1D);
  const [customRange, setCustomRange] = useState(defaultCustomRange);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_PERF_FILTERS);
  const [draftFilters, setDraftFilters] = useState(DEFAULT_PERF_FILTERS);
  const [detailTarget, setDetailTarget] = useState(null);

  const listQuery = useGetApiServiceListQuery(
    { account: baseAccount?.toLowerCase() === 'all' ? '*' : baseAccount, limit: 5000, offset: 0 },
    { skip: !baseAccount }
  );

  const serviceRows = useMemo(() => {
    if (!metricsSuccess(listQuery.data)) return [];
    return (listQuery.data?.data?.rows || []).map((r) => ({
      name: r.service_name ?? r.wf_name,
      workflowGroup: r.wf_group,
      workflowName: r.wf_name,
      adapter: r.adapter_type,
      account: r.account,
    }));
  }, [listQuery.data]);

  const serviceNameMap = useMemo(() => buildServiceNameMap(serviceRows), [serviceRows]);
  const serviceAccountMap = useMemo(() => buildServiceAccountMap(serviceRows), [serviceRows]);

  const filterOptions = useMemo(
    () => buildFilterOptionsFromServices(serviceRows, appliedFilters.workflowGroups),
    [serviceRows, appliedFilters.workflowGroups]
  );

  const effectiveRange = useMemo(() => {
    if (timeframe === TIMEFRAME_CUSTOM) return customRange;
    return rangeForPreset(timeframe);
  }, [timeframe, customRange]);

  const metricsParams = useMemo(
    () =>
      buildMetricsQueryParams({
        baseAccount,
        filters: appliedFilters,
        timeRange: effectiveRange,
        quantity: appliedFilters.quantity,
      }),
    [baseAccount, appliedFilters, effectiveRange]
  );

  const rollingParams = useMemo(() => {
    const params = { ...metricsParams, include_by_source: true };
    if (timeframe === TIMEFRAME_1D) {
      delete params.start_date;
      delete params.end_date;
    }
    return params;
  }, [metricsParams, timeframe]);

  const rollingQuery = useGetApiServiceMetricsRolling24hQuery(
    baseAccount ? rollingParams : skipToken,
    { refetchOnMountOrArgChange: true }
  );

  const rollingPayload = rollingQuery.currentData ?? rollingQuery.data;

  const bySourceRows = useMemo(() => {
    if (!metricsSuccess(rollingPayload)) return [];
    return rollingPayload?.data?.by_source || [];
  }, [rollingPayload]);

  const outlierRows = useMemo(() => {
    if (!metricsSuccess(rollingPayload)) return [];
    return aggregateBySourceToOutliers(rollingPayload?.data?.by_source || []);
  }, [rollingPayload]);

  const sourceChart = useMemo(
    () =>
      transformBySourceToChart(
        bySourceRows,
        appliedFilters.quantity,
        serviceNameMap,
        serviceAccountMap
      ),
    [bySourceRows, appliedFilters.quantity, serviceNameMap, serviceAccountMap]
  );

  const violationLabels = useMemo(
    () => ({
      execTime: t(K.API_SVC_PERF_EXEC_TIME),
      execTimeTooltip: t(K.API_SVC_PERF_EXEC_TIME_ERRORS),
      validation: t(K.API_SVC_PERF_VALIDATION),
      translation: t(K.API_SVC_PERF_TRANSLATION),
      errors: t(K.API_SVC_PERF_ERRORS),
    }),
    [t]
  );

  const outlierChart = useMemo(
    () =>
      transformOutliersToChart(
        outlierRows,
        appliedFilters.quantity,
        serviceNameMap,
        violationLabels,
        serviceAccountMap
      ),
    [outlierRows, appliedFilters.quantity, serviceNameMap, serviceAccountMap, violationLabels]
  );

  const chartsLoading = isMetricsQueryPending(rollingQuery, rollingPayload);

  const apiFilterLocked = !isAllSelected(appliedFilters.apis);

  const hasActiveFilters = !filtersAreDefault(appliedFilters, timeframe, customRange);

  const handleBarClick = useCallback(
    (bar) => {
      setDetailTarget(buildDetailTargetFromBar(bar, serviceRows));
    },
    [serviceRows]
  );

  const handleApplyFilters = (next) => {
    setAppliedFilters(next);
    setDraftFilters(next);
  };

  const handleClearFilters = () => {
    setAppliedFilters(DEFAULT_PERF_FILTERS);
    setDraftFilters(DEFAULT_PERF_FILTERS);
    setTimeframe(TIMEFRAME_1D);
    setCustomRange(defaultCustomRange());
  };

  const handleTimeframeChange = (tf) => {
    setTimeframe(tf);
    if (tf !== TIMEFRAME_CUSTOM) {
      setCustomRange(rangeForPreset(tf));
    }
  };

  useEffect(() => {
    if (!seedApi) return;
    setDetailTarget({
      label: seedApi.name,
      workflowGroup: seedApi.workflowGroup,
      workflowName: seedApi.workflowName,
      account: seedApi.account,
    });
    onSeedConsumed?.();
  }, [seedApi, onSeedConsumed]);

  return (
    <Box>
      <ApiServicePerformanceToolbar
        timeframe={timeframe}
        onTimeframeChange={handleTimeframeChange}
        customRange={customRange}
        onCustomRangeChange={(range) => {
          setCustomRange(range);
          setTimeframe(TIMEFRAME_CUSTOM);
        }}
        draftFilters={draftFilters}
        onDraftFiltersChange={setDraftFilters}
        filterOptions={filterOptions}
        hasActiveFilters={hasActiveFilters}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        apiFilterLocked={apiFilterLocked}
      />

      {rollingQuery.error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t(K.API_SVC_PERF_LOAD_FAILED)}
        </Alert>
      )}

      <Stack spacing={3} key={timeframe}>
        <ApiServiceStackedColumnChart
          title={t(K.API_SVC_PERF_CHART_SOURCE)}
          yAxisLabel={t(K.API_SVC_PERF_Y_TRANSACTIONS)}
          xAxisLabel={t(K.API_SVC_PERF_X_API)}
          series={sourceChart.series}
          bars={sourceChart.bars}
          loading={chartsLoading}
          loadingMessage={t(K.API_SVC_PERF_LOADING)}
          emptyMessage={t(K.API_SVC_PERF_NO_DATA)}
          onBarClick={handleBarClick}
          renderTooltip={({ bar, series, value }) => (
            <>
              <div>
                <strong>{bar.label}</strong>
              </div>
              <div>
                {t(K.API_SVC_PERF_SOURCE)}: {series.label}
              </div>
              <div>
                {t(K.API_SVC_PERF_REQUESTS)}: {value}
              </div>
              <div>
                {t(K.API_SVC_PERF_TOTAL_REQUESTS)}: {bar.total}
              </div>
            </>
          )}
        />

        <ApiServiceStackedColumnChart
          title={t(K.API_SVC_PERF_CHART_OUTLIERS)}
          yAxisLabel={t(K.API_SVC_PERF_Y_VIOLATIONS)}
          xAxisLabel={t(K.API_SVC_PERF_X_API)}
          series={outlierChart.series}
          bars={outlierChart.bars}
          loading={chartsLoading}
          loadingMessage={t(K.API_SVC_PERF_LOADING)}
          emptyMessage={t(K.API_SVC_PERF_NO_OUTLIERS)}
          onBarClick={handleBarClick}
          renderTooltip={({ bar, series, value }) => (
            <>
              <div>
                <strong>{bar.label}</strong>
              </div>
              <div>
                {t(K.API_SVC_PERF_TOTAL_ERRORS)}: {bar.total}
              </div>
              <div>
                {series.tooltipLabel || series.label}: {value}
              </div>
              {bar.transCount > 0 && (
                <div>
                  {t(K.API_SVC_PERF_TOTAL_REQUESTS)}: {bar.transCount}
                </div>
              )}
            </>
          )}
        />
      </Stack>

      <ApiServicePerformanceSingularView
        target={detailTarget}
        onClose={() => setDetailTarget(null)}
      />
    </Box>
  );
}

export default memo(ApiServicePerformanceDashboard);
